import React from 'react'
import { dataIdFromObject } from '@sketch/graphql-cache'
import { ShareForSettingsModal } from '../types'
import {
  useGetShareMembershipsQuery,
  ShareMembershipInfoFragment,
} from '@sketch/gql-types'
import { Box, handleFetchMore, InfiniteList } from '@sketch/components'
import { ShareMember, ShareMemberPlaceholder } from '../ShareMember'
import { ShareMemberWrapper } from './ShareMemberships.styles'

export type ShareForMembershipList = Pick<
  ShareForSettingsModal,
  | 'identifier'
  | 'userCanUpdateCommentsEnabled'
  | 'userCanUpdateSettings'
  | 'type'
  | 'parentAccessLevel'
  | 'isDraft'
  | 'name'
  | 'memberships'
>

interface MembershipListProps {
  share: ShareForMembershipList
  guestsOnly?: boolean
  excludeGuests?: boolean
}

const PAGE_SIZE = 20

const buildPlaceholderArray = (count: number) => (
  <>
    {new Array(count).fill(null).map((_, index) => (
      <ShareMemberPlaceholder key={index} />
    ))}
  </>
)

const ShareMemberships = ({
  share,
  guestsOnly,
  excludeGuests,
}: MembershipListProps) => {
  const membershipCount = share.memberships?.meta.totalCount

  const { error, data, loading, fetchMore } = useGetShareMembershipsQuery({
    fetchPolicy: 'cache-and-network',
    variables: {
      shareIdentifier: share.identifier,
      // @TODO: this filter will be possible after Cloud#17164
      //   filter: {type: guestsOnly ? 'GUEST' : undefined},
    },
  })

  const membershipsData = (data?.share?.memberships?.entries ??
    []) as ShareMembershipInfoFragment[]

  const memberships = membershipsData.filter(
    // @TODO: this manual filter will be removed after Cloud#17164
    (member: any) => {
      if (excludeGuests) {
        return member.workspaceMembership.role !== 'GUEST'
      } else if (guestsOnly) {
        return member.workspaceMembership.role === 'GUEST'
      }
      return true
    }
  )
  const myMembership = data?.share?.myMembership

  if (error) {
    return (
      <Box my={4} color="red">
        Memberships failed to load
      </Box>
    )
  }

  if (loading) {
    return buildPlaceholderArray(2)
  }

  if (memberships.length === 0) {
    return null
  }

  const after = data?.share?.memberships?.meta.after || null
  const placeholderMembers = (membershipCount || PAGE_SIZE) - memberships.length

  return (
    <InfiniteList
      wrappingComponent={React.Fragment}
      onLoadMore={handleFetchMore(
        fetchMore,
        ['share', 'memberships', 'entries'],
        { dataIdFromObject, after }
      )}
      renderLoading={() => null}
    >
      {memberships.map((membership: ShareMembershipInfoFragment) => (
        <ShareMemberWrapper key={membership.identifier}>
          <ShareMember
            share={share}
            member={membership}
            isMyMembership={myMembership?.identifier === membership.identifier}
          />
        </ShareMemberWrapper>
      ))}
      {loading &&
        buildPlaceholderArray(Math.min(PAGE_SIZE, placeholderMembers))}
    </InfiniteList>
  )
}

export default ShareMemberships
